import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, Collapse, Divider, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import React, { Fragment, memo, useContext, useMemo, useState } from "react";
import { ReactComponent as LinkedInIcon } from "../../../assets/icons/linkedin.svg";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from '../../../theme';
import { Dashboard } from "../../../types/files";
import MultiSelector from '../../atoms/MultiSelector';

const useStyles = makeStyles((theme) => ({
    card: {
        width: 450,
        maxWidth: 450,
        minHeight: 200,
        maxHeight: 400,
        height: 'auto',
        overflow: 'hidden',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    keyPeople: {
        width: '100%',
        minHeight: 30,
        background: theme.colors.warning['100'],
        paddingLeft: 16,
    },
    container: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...scrollbarStyle,
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['600'],
        maxWidth: '24ch',
        textOverflow: 'ellipsis',
    },
    masonryElement: {
        width: '100%',
        height: 'fit-content',
        padding: '12px 24px 8px 16px',
        transition: 'all 300ms ease',
    },
    personIcon: {
        width: 24,
        height: 24,
    },
    icon: {
        width: 24,
        height: 24,
        fill: '#7bd4d4',
    },
    link: {
        display: 'inline-block',
        fontFamily: 'Inter !important',
        fontSize: '1rem',
        fontWeight: 'bold',
        textDecoration: 'none',
        color: theme.palette.primary.main,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
    },
    email: {
        display: 'inline-block',
        fontFamily: 'Inter !important',
        fontSize: '0.95rem',
        textDecoration: 'none',
        color: theme.colors.neutral['500'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
    },
    answer: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        color: '#666666',
        textTransform: 'none !important' as any,
        whiteSpace: 'pre-line',
    },
    collapsableText: {
        width: '100%',
        padding: '4px 0',
        cursor: 'text',
        transition: 'all 300ms ease',
    },
    textClamp: {
        color: '#666666',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: '3',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}));

const PersonCard: React.FC<{ element: any }> = ({ element }) => {
    const classes = useStyles();
    const [isExpanded, setExpanded] = useState<boolean>(false);

    return (<>
        <Stack className={classes.masonryElement}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" minWidth="80%"
                    sx={{ cursor: !!element.link ? 'pointer' : 'auto' }}
                    onClick={!!element.link ? (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        window.open(element.link, '_blank', 'noopener, noreferrer');
                    } : undefined}>
                    <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24, fill: "rgb(163, 167, 172)" }} />
                    <Typography className={classes.link}>
                        {element.title.split('|')?.[0]?.split('-')?.[0]}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" width="25%">
                    {!!element.link && element.link?.includes('linkedin') && (
                        <IconButton onClick={(e) =>  {
                            e.stopPropagation();
                            e.preventDefault();
                            window.open(element.link, '_blank', 'noopener, noreferrer');
                        }} sx={{ padding: 0 }} disableRipple>
                            <LinkedInIcon className={classes.icon} />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
            {!!element.email && (
                <Typography className={classes.email} ml={4}>
                    {element.email}
                </Typography>
            )}
            {!!element.role && (
                <Typography color="#666666" fontWeight={600} ml={4}>
                    {element.role}
                </Typography>
            )}
            {!!element.tags?.length && (
                <Box ml={4} my="4px">
                    <MultiSelector
                        mainOptions={element.tags}
                        selections={element.tags}
                        cropped={350}
                        readOnly />
                </Box>
            )}
            {!!element.content && (
                <Stack spacing="4px" alignItems="flex-start" justifyContent="flex-start" width="100%" mt={1}>
                    <Typography color={theme.colors.neutral['500']} fontWeight={600}>
                        {'Background '}
                    </Typography>
                    <Collapse in={isExpanded} collapsedSize="100%" sx={{ width:'100%', height: '100%' }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" width="100%" height="100%">
                            <Box className={classNames(classes.collapsableText, !isExpanded ? classes.textClamp : {})}
                                sx={{ WebkitLineClamp: !isExpanded ? '3' : 'unset' }}
                                onClick={(e)=>{
                                    e.preventDefault();
                                    setExpanded((prev) => !prev);
                                }}>
                                <Typography className={classes.answer}>
                                    {element.content?.replace('- ', '• ')?.replaceAll('\n-', '\n •')}
                                </Typography>
                            </Box>
                            <Stack direction="column" alignItems="center" justifyContent="flex-end" width={24} height="100%">
                                <IconButton onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setExpanded((prev) => !prev);
                                    }} sx={{ paddingBottom: 'unset'}}
                                    disableRipple>
                                    <ExpandMoreIcon sx={{ width: 32, height: 32,
                                        color: theme.colors.neutral['500'],
                                        transform: `rotate(${isExpanded ? 180 : 0}deg)` }} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Collapse>
                </Stack>
            )}
        </Stack>
    </>);
};

const KeyPeopleContentCard: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { mappedOverviewQueries } = useContext(DashboardsContext);

    const keyPeople = useMemo(() =>
        mappedOverviewQueries.get(`${dashboard.id}:Key People`)
    , [dashboard.id, mappedOverviewQueries]);

    if (!Array.isArray(keyPeople) || keyPeople.length === 0)
        return (<></>);

    return (<>
        <Stack className={classes.card}>
            <Stack className={classes.keyPeople} justifyContent="center">
                <Typography fontFamily="Inter" fontSize="0.8rem" fontWeight={700}
                    color={theme.colors.orange['600']}>
                    {'Team'}
                </Typography>
            </Stack>
            <Stack className={classes.container} alignItems="flex-start" justifyContent="stretch">
                {keyPeople?.map((element: any, i) => (
                    <Fragment key={'key-people-content-card-196-' + i}>
                        {(i > 0) && <Divider sx={{ width: '100%' }} />}
                        <PersonCard element={element} />
                    </Fragment>
                ))}
            </Stack>
        </Stack>
    </>);
};

export default memo(KeyPeopleContentCard);
